import React from 'react';
import { Link, Box } from '@chakra-ui/react';
import { decode } from 'js-base64';
import { KText } from '@/ui-library/components';

/**
 * Hook to extract and format common requisition data from notification
 * @param {Object} notification - The notification object
 * @returns {Object} - Object containing requisition data and common details array
 */
export const useRequisitionData = (notification = {}) => {
  const { custom_attributes } = notification;
  const data = JSON.parse(decode(custom_attributes || '{}'));

  // Extract common requisition fields
  const {
    requisition_id,
    requisition_ids,
    requisition_code,
    requisition_codes,
    role_name,
    description,
    office_names,
    employment_type,
    requisition_type,
    department_name,
    target_hire_date,
    target_start_date,
    salary,
    hiring_manager_name,
    recruiter_name,
    requester_name,
    submitted_by_name,
    approval_status,
    head_count,
    ...rest
  } = data || {};

  /**
   * Build common requisition details array
   * @param {Array} additionalFields - Additional fields to append to the common details
   * @param {Object} options - Options object
   * @param {Array} options.excludeFields - Array of field labels to exclude from common details
   * @returns {Array} - Array of {label, value} objects
   */
  const buildRequisitionDetails = (
    additionalFields = [],
    options = { excludeFields: [] }
  ) => {
    const { excludeFields = [] } = options;
    // Build link URL for Req ID if requisition_id is available
    const reqIdLink = requisition_id
      ? `/requisitions?view=${requisition_id}`
      : null;
    const commonDetails = [
      head_count && {
        label: 'Head count',
        value: head_count,
      },
      requisition_code &&
        !requisition_ids && {
          label: 'Req ID',
          value: requisition_code,
          href: reqIdLink,
        },
      requisition_codes &&
        requisition_ids && {
          label: 'Req IDs',
          value: (() => {
            // Handle requisition_codes as comma-separated string or array
            const codes = Array.isArray(requisition_codes)
              ? requisition_codes
              : typeof requisition_codes === 'string'
              ? requisition_codes.split(',').map((code) => code.trim())
              : [requisition_codes];

            // Handle requisition_ids as array or single value
            const ids = Array.isArray(requisition_ids)
              ? requisition_ids
              : [requisition_ids];

            return (
              <Box display="inline">
                {codes.map((code, index) => {
                  const reqId = ids[index];
                  const link = reqId ? `/requisitions?view=${reqId}` : null;

                  return (
                    <React.Fragment key={`req-code-${index}-${reqId}`}>
                      {index > 0 && ', '}
                      {link ? (
                        <Link href={link} target="_blank" display="inline">
                          <KText
                            size="sm-medium"
                            _hover={{ textDecoration: 'underline' }}
                            as="span"
                            _active={{
                              border: 'none !important',
                            }}
                          >
                            {code}
                          </KText>
                        </Link>
                      ) : (
                        <span>{code}</span>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            );
          })(),
        },
      { label: 'Role name', value: role_name },
      { label: 'Description', value: description },
      { label: 'Department', value: department_name },
      { label: 'Office', value: office_names },
      { label: 'Work type', value: employment_type },
      { label: 'Requisition type', value: requisition_type },
      { label: 'Salary', value: salary },
      { label: 'Recruitment partner', value: recruiter_name },
      { label: 'Hiring manager', value: hiring_manager_name },
      { label: 'Target hire date', value: target_hire_date },
      { label: 'Target start date', value: target_start_date },
    ]
      .filter(Boolean)
      .filter((field) => !excludeFields.includes(field.label));

    return [...commonDetails, ...additionalFields];
  };

  return {
    data,
    requisition_id,
    requisition_ids,
    requisition_code,
    requisition_codes,
    role_name,
    description,
    head_count,
    office_names,
    employment_type,
    requisition_type,
    department_name,
    target_hire_date,
    target_start_date,
    salary,
    hiring_manager_name,
    recruiter_name,
    requester_name,
    submitted_by_name,
    approval_status,
    ...rest, // Return all other fields for component-specific use
    buildRequisitionDetails,
  };
};
